// TODO: refactor -- CSRF is now handled globally in main.jsx; these helpers can be
// removed once all api.js callers (saveTask, saveTimeLog, saveComment, etc.) are
// migrated to DataSync/rjbuild.
import { decodeHtmlEntities } from '../utils/html';
import { ensureCsrfToken, resetCsrfToken } from './csrf.js';

export { resetCsrfToken };

async function getHeaders(forceRefresh = false) {
  if (forceRefresh) resetCsrfToken();
  const csrfToken = await ensureCsrfToken();
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-CSRF-Token': csrfToken,
  };
}

/**
 * Generic save via decoupled_toolbox Write API.
 * @param {string} entityType - e.g. 'group', 'node', 'comment'
 * @param {string} bundle - e.g. 'project', 'task', 'time_log', 'account'
 * @param {Object} data - field values using decoupled_field_key names
 * @param {number|null} entityId - null for create, ID for update
 */
export async function decoupledSave(entityType, bundle, data, entityId = null, extraParams = {}) {
  const params = new URLSearchParams({
    display: 'decoupled',
    entity_type: entityType,
    bundle: bundle,
  });
  if (entityId) {
    params.set('entity_id', entityId);
  }
  for (const [k, v] of Object.entries(extraParams)) {
    if (v != null) params.set(k, v);
  }

  const url = `/decoupled-api/save?${params}`;
  let headers = await getHeaders();

  let response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ data }),
  });

  if (response.status === 403) {
    headers = await getHeaders(true);
    response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ data }),
    });
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const msg = errorBody?.status?.message || response.statusText;
    throw new Error(`Save failed (${response.status}): ${msg}`);
  }

  return response.json();
}

/**
 * Generic delete via decoupled_toolbox Write API.
 */
export async function decoupledDelete(entityType, bundle, entityId) {
  const params = new URLSearchParams({
    display: 'decoupled',
    entity_type: entityType,
    bundle: bundle,
    id: entityId,
  });

  const url = `/decoupled-api/delete?${params}`;
  let headers = await getHeaders();

  let response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
  });

  if (response.status === 403) {
    headers = await getHeaders(true);
    response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
    });
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const msg = errorBody?.status?.message || response.statusText;
    throw new Error(`Delete failed (${response.status}): ${msg}`);
  }

  return response.json();
}

/**
 * Lecture générique d'une collection via decoupled_toolbox.
 *
 * Troisième opération du contrat, à côté de `decoupledSave` et
 * `decoupledDelete`. Quatre pièges y sont encapsulés une fois pour toutes :
 *
 *  - la pagination s'écrit `limit` (+ `offset`) ; `range`, que l'on croit
 *    volontiers être le nom du paramètre, est ignoré en silence et la
 *    collection revient ENTIÈRE ;
 *  - un filtre se pose en `filter[i][f|v|c]`, où l'opérateur est la clé `c` —
 *    avec `op`, la condition est ignorée sans erreur ;
 *  - `f` et `sort` désignent le NOM DU CHAMP DRUPAL (`field_def_kind`,
 *    `title`), jamais la clé decoupled rendue (`kind`, `name`) ;
 *  - une valeur de filtre vide ou `0` est refusée côté serveur (`empty()`) :
 *    ces conditions ne sont pas envoyées.
 *
 * @param {string} entityType
 * @param {string} bundle
 * @param {Object} [options]
 * @param {string} [options.display]  View mode (défaut `decoupled`).
 * @param {Array<[string, *]|[string, *, string]>} [options.filters]
 *   Conditions `[champ, valeur]` ou `[champ, valeur, opérateur]`.
 * @param {string} [options.sort]     Champ de tri, préfixé de `-` pour décroissant.
 * @param {number} [options.limit]
 * @param {number} [options.offset]
 * @returns {Promise<Array>} Les items bruts (voir `unwrapSyncable`).
 */
export async function decoupledCollection(entityType, bundle, options = {}) {
  const { display = 'decoupled', filters = [], sort, limit, offset } = options;

  const params = new URLSearchParams({ display });
  if (sort) params.set('sort', sort);
  if (limit != null) params.set('limit', String(limit));
  if (offset != null) params.set('offset', String(offset));

  let i = 0;
  for (const [field, value, operator] of filters) {
    if (value === undefined || value === null || value === '') continue;
    params.append(`filter[${i}][f]`, field);
    params.append(`filter[${i}][v]`, String(value));
    if (operator) params.append(`filter[${i}][c]`, operator);
    i += 1;
  }

  const res = await fetch(`/decoupled-api/${entityType}/${bundle}/collection?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Collection failed (${res.status})`);

  const rows = await res.json();
  return Array.isArray(rows) ? rows : [];
}

/**
 * Lecture d'un item d'une collection, quelle que soit sa forme.
 *
 * Un item que le lecteur peut modifier arrive enveloppé
 * (`{item_url, submission_url, data: {...}}`) ; un item qu'il ne peut que lire
 * arrive À PLAT (`{item_url, id, name, ...}`). Un `.map(u => u.data.name)`
 * marche donc sur ses propres fiches et rend `undefined` sur celles des autres,
 * sans erreur.
 */
export function unwrapSyncable(item) {
  return item && typeof item === 'object' ? (item.data ?? item) : item;
}

// ---------------------------------------------------------------------------
// Orbit Trash API — soft-delete, list, restore, purge

// ---------------------------------------------------------------------------

/**
 * Soft-delete an entity with cascade (uses /api/orbit/trash).
 */
export async function trashEntity(entityType, bundle, entityId) {
  const url = '/api/orbit/trash';
  let headers = await getHeaders();

  let response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ entity_type: entityType, bundle, id: entityId }),
  });

  if (response.status === 403) {
    headers = await getHeaders(true);
    response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ entity_type: entityType, bundle, id: entityId }),
    });
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const msg = errorBody?.status?.message || response.statusText;
    throw new Error(`Trash failed (${response.status}): ${msg}`);
  }

  return response.json();
}

/**
 * List trashed entities.
 * @param {'doc'|'task'|'project'|'all'} scope
 * @param {'me'|'all'} user
 */
export async function listTrashed(scope = 'all', user = 'me') {
  const params = new URLSearchParams({ scope, user });
  const res = await fetch(`/api/orbit/trash/list?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`List trash failed (${res.status})`);
  return res.json();
}

/**
 * Restore a trashed entity with cascade.
 */
export async function restoreEntity(entityType, bundle, entityId) {
  const url = '/api/orbit/trash/restore';
  let headers = await getHeaders();

  let response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ entity_type: entityType, bundle, id: entityId }),
  });

  if (response.status === 403) {
    headers = await getHeaders(true);
    response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ entity_type: entityType, bundle, id: entityId }),
    });
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const msg = errorBody?.status?.message || response.statusText;
    throw new Error(`Restore failed (${response.status}): ${msg}`);
  }

  return response.json();
}

/**
 * Permanently delete a trashed entity (admin only).
 */
export async function purgeEntity(entityType, bundle, entityId) {
  const url = '/api/orbit/trash/purge';
  let headers = await getHeaders();

  let response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ entity_type: entityType, bundle, id: entityId }),
  });

  if (response.status === 403) {
    headers = await getHeaders(true);
    response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ entity_type: entityType, bundle, id: entityId }),
    });
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const msg = errorBody?.status?.message || response.statusText;
    throw new Error(`Purge failed (${response.status}): ${msg}`);
  }

  return response.json();
}

/**
 * Soft-delete a project (moves to trash with task cascade).
 */
export async function deleteProject(projectId) {
  return trashEntity('group', 'project', projectId);
}

// ---------------------------------------------------------------------------
// Orbit Archive API — archive, list, unarchive
// ---------------------------------------------------------------------------

/**
 * Archive an entity with cascade (uses /api/orbit/archive).
 */
export async function archiveEntity(entityType, bundle, entityId) {
  const url = '/api/orbit/archive';
  let headers = await getHeaders();

  let response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ entity_type: entityType, bundle, id: entityId }),
  });

  if (response.status === 403) {
    headers = await getHeaders(true);
    response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ entity_type: entityType, bundle, id: entityId }),
    });
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const msg = errorBody?.status?.message || response.statusText;
    throw new Error(`Archive failed (${response.status}): ${msg}`);
  }

  return response.json();
}

/**
 * Unarchive an entity with cascade.
 */
export async function unarchiveEntity(entityType, bundle, entityId) {
  const url = '/api/orbit/archive/unarchive';
  let headers = await getHeaders();

  let response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ entity_type: entityType, bundle, id: entityId }),
  });

  if (response.status === 403) {
    headers = await getHeaders(true);
    response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ entity_type: entityType, bundle, id: entityId }),
    });
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const msg = errorBody?.status?.message || response.statusText;
    throw new Error(`Unarchive failed (${response.status}): ${msg}`);
  }

  return response.json();
}

export async function archiveProject(projectId) {
  return archiveEntity('group', 'project', projectId);
}

export async function archiveTask(taskId) {
  return archiveEntity('node', 'task', taskId);
}

export async function unarchiveTask(taskId) {
  return unarchiveEntity('node', 'task', taskId);
}

export async function archiveDocDirectory(dirId) {
  return archiveEntity('taxonomy_term', 'doc_directory', dirId);
}

export async function archiveDocPage(pageId) {
  return archiveEntity('node', 'builder_page', pageId);
}

/**
 * Build a label→TID map from an options array [{value, label}, ...].
 * Falls back to the provided static map if options is empty/undefined.
 */
function buildMapFromOptions(options, fallbackMap = {}) {
  if (!Array.isArray(options) || options.length === 0) return fallbackMap;
  const map = {};
  for (const o of options) {
    if (o.label && o.value) map[o.label] = parseInt(o.value, 10);
  }
  return map;
}

// Static fallback maps (used only when dynamic options are not available).
const TASK_STATUS_MAP_STATIC = {};
const TASK_TYPE_MAP_STATIC = {};
const RETURN_TYPE_MAP_STATIC = {};
const EVOLUTION_TYPE_MAP_STATIC = {};
const TASK_PROFILE_MAP_STATIC = {};

function resolveFieldId(value, map) {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    if (/^\d+$/.test(value)) return parseInt(value, 10);
    if (map[value] !== undefined) return map[value];
    const decoded = decodeHtmlEntities(value);
    if (decoded !== value && map[decoded] !== undefined) return map[decoded];
  }
  return value;
}

/**
 * Save a task (node/task).
 * Accepts both decoupled API keys and legacy KanbanBoard field names.
 *
 * Dynamic options (preferred — pass taxonomy options loaded from API):
 *   taskData._statusOptions  – [{value, label}] from task_status taxonomy
 *   taskData._typeOptions    – [{value, label}] from task_type taxonomy
 *   taskData._returnTypeOptions – [{value, label}] from return_type taxonomy
 *   taskData._evolutionTypeOptions – [{value, label}] from evolution_type taxonomy
 *   taskData._profileOptions – [{value, label}] from task_profile taxonomy
 *   taskData._columns        – [{id, title}] YAML columns (for column-ID→status resolution)
 *
 * @deprecated Kanban board view uses persistTask() + DataSync; kept for TaskEdit and ListView.
 */
export async function saveTask(taskData) {
  const data = {};

  const statusMap = buildMapFromOptions(taskData._statusOptions, TASK_STATUS_MAP_STATIC);
  const typeMap = buildMapFromOptions(taskData._typeOptions, TASK_TYPE_MAP_STATIC);
  const returnTypeMap = buildMapFromOptions(taskData._returnTypeOptions, RETURN_TYPE_MAP_STATIC);
  const evolutionTypeMap = buildMapFromOptions(taskData._evolutionTypeOptions, EVOLUTION_TYPE_MAP_STATIC);
  const profileMap = buildMapFromOptions(taskData._profileOptions, TASK_PROFILE_MAP_STATIC);

  if (taskData.title) data.title = taskData.title;
  if (taskData.description !== undefined) data.description = taskData.description;

  const rawStatus = taskData.status || taskData.task_status;
  if (rawStatus !== undefined) {
    if (typeof rawStatus === 'number' && taskData._columns) {
      const col = taskData._columns.find(c => c.id === rawStatus);
      if (col) {
        data.status = resolveFieldId(col.title, statusMap);
      } else {
        data.status = resolveFieldId(rawStatus, statusMap);
      }
    } else {
      data.status = resolveFieldId(rawStatus, statusMap);
    }
  }

  const rawType = taskData.type || taskData.task_type;
  if (rawType !== undefined) data.type = resolveFieldId(rawType, typeMap);

  if (taskData.priority !== undefined) {
    const priorityMap = { 'Low': 1, 'Medium': 3, 'High': 5, 'Critical': 7 };
    if (typeof taskData.priority === 'string') {
      data.priority = priorityMap[taskData.priority] || 3;
    } else if (Number.isInteger(taskData.priority) && taskData.priority >= 1) {
      data.priority = taskData.priority;
    }
    // numeric 0/unset → OMIT (field min:1; keeps empty on create, unchanged on edit).
  }

  const projectId = taskData.projectId || taskData.project;
  if (projectId) data.projectId = projectId;

  const assignee = taskData.assignee || taskData.assigned_to;
  if (assignee) data.assignee = assignee;

  const parentId = taskData.parentTaskId || taskData.parent_task;
  if (parentId) data.parentTaskId = parentId;

  if (taskData.estimation !== undefined) data.estimation = taskData.estimation;
  if (taskData.dueDate) data.dueDate = taskData.dueDate.substring(0, 10);
  // complexity OPTIONAL: OMIT when unset (field min:1 → sending 0 = HTTP 422).
  if (Number.isInteger(taskData.complexity) && taskData.complexity >= 1) data.complexity = taskData.complexity;
  if (taskData.isUrgent !== undefined) data.isUrgent = taskData.isUrgent;
  // Deferred file attachments (fids of temporary files uploaded in the modal).
  if (Array.isArray(taskData.attachedFiles) && taskData.attachedFiles.length) data.attachedFiles = taskData.attachedFiles;

  const rawReturnType = taskData.returnType;
  if (rawReturnType) data.returnType = resolveFieldId(rawReturnType, returnTypeMap);

  const rawEvolutionType = taskData.evolutionType;
  if (rawEvolutionType) data.evolutionType = resolveFieldId(rawEvolutionType, evolutionTypeMap);

  const rawProfile = taskData.profile;
  if (rawProfile) data.profile = resolveFieldId(rawProfile, profileMap);

  if (taskData.tags !== undefined) {
    data.tags = Array.isArray(taskData.tags)
      ? taskData.tags.map(t => resolveFieldId(t, {}))
      : taskData.tags;
  }

  const entityId = taskData.id || null;
  return decoupledSave('node', 'task', data, entityId);
}

/**
 * Soft-delete a task (moves to trash with sub-task cascade).
 */
export async function deleteTask(taskId) {
  return trashEntity('node', 'task', taskId);
}

/**
 * Save a time log (node/time_log).
 * TODO: Remove — time logs are now managed via DataSync in TaskEdit's reactive-json config.
 */
export async function saveTimeLog(logData) {
  const data = {};

  data.title = logData.title || `Time log - ${logData.date || new Date().toISOString().split('T')[0]}`;
  if (logData.description) data.description = logData.description;
  if (logData.date) data.date = logData.date;
  if (logData.timeSpent !== undefined) data.timeSpent = logData.timeSpent;
  if (logData.timeLeft !== undefined) data.timeLeft = logData.timeLeft;
  if (logData.projectId) data.projectId = logData.projectId;
  if (logData.taskId) data.taskId = logData.taskId;
  if (logData.user) data.user = logData.user;

  const entityId = logData.id || null;
  return decoupledSave('node', 'time_log', data, entityId);
}

/**
 * Delete a time log.
 * TODO: Remove — time log deletion is now handled via submitData in TaskEdit's reactive-json config.
 */
export async function deleteTimeLog(logId) {
  return decoupledDelete('node', 'time_log', logId);
}

/**
 * Save a comment (comment/task_comment).
 */
export async function saveComment(commentData) {
  const data = {};

  if (commentData.text) data.text = commentData.text;
  if (commentData.subject) data.subject = commentData.subject;
  if (commentData.entityId) data.entityId = commentData.entityId;
  data.commentedEntityType = commentData.commentedEntityType || 'node';
  data.fieldName = commentData.fieldName || 'field_comments';

  const entityId = commentData.id || null;
  return decoupledSave('comment', 'task_comment', data, entityId);
}

/**
 * Delete a comment.
 */
export async function deleteComment(commentId) {
  return decoupledDelete('comment', 'task_comment', commentId);
}

/**
 * Save a history entry (comment/task_history).
 */
export async function saveHistory(historyData) {
  const data = {};

  if (historyData.text) data.text = historyData.text;
  data.subject = new Date().toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' });
  if (historyData.action) data.action = historyData.action;
  if (historyData.entityId) data.entityId = historyData.entityId;
  data.commentedEntityType = historyData.commentedEntityType || 'node';
  data.fieldName = historyData.fieldName || 'field_history';

  const entityId = historyData.id || null;
  return decoupledSave('comment', 'task_history', data, entityId);
}

export async function deleteHistory(historyId) {
  return decoupledDelete('comment', 'task_history', historyId);
}

/**
 * Save a doc directory (taxonomy_term/doc_directory).
 */
export async function saveDocDirectory(directoryData) {
  const data = {};
  if (directoryData.name) data.name = directoryData.name;
  if (directoryData.description !== undefined) data.description = directoryData.description;
  if (directoryData.projectId !== undefined) data.projectId = directoryData.projectId;
  if (directoryData.parentId !== undefined) data.parentId = directoryData.parentId;
  if (directoryData.weight !== undefined) data.weight = directoryData.weight;
  if (directoryData.accessGrants !== undefined) data.accessGrants = directoryData.accessGrants;
  if (directoryData.overrideAccess !== undefined) data.overrideAccess = directoryData.overrideAccess;
  if (directoryData.spaceCategoryIds !== undefined) data.spaceCategoryIds = directoryData.spaceCategoryIds;
  if (directoryData.wikiGroupIds !== undefined) data.wikiGroupIds = directoryData.wikiGroupIds;
  if (directoryData.ownerId !== undefined) data.ownerId = directoryData.ownerId;

  const entityId = directoryData.id || null;
  const extra = (!entityId && data.parentId) ? { parent: data.parentId } : {};
  return decoupledSave('taxonomy_term', 'doc_directory', data, entityId, extra);
}

/**
 * Soft-delete a doc directory (moves to trash with cascade).
 */
export async function deleteDocDirectory(directoryId) {
  return trashEntity('taxonomy_term', 'doc_directory', directoryId);
}

/**
 * Save a doc space category (taxonomy_term/doc_space_category).
 */
export async function saveDocSpaceCategory(catData) {
  const data = {};
  if (catData.name) data.name = catData.name;
  if (catData.parentId !== undefined) data.parentId = catData.parentId;
  if (catData.description !== undefined) data.description = catData.description;
  if (catData.weight !== undefined) data.weight = catData.weight;
  return decoupledSave('taxonomy_term', 'doc_space_category', data, catData.id || null);
}

/**
 * Delete a doc space category.
 */
export async function deleteDocSpaceCategory(categoryId) {
  return decoupledDelete('taxonomy_term', 'doc_space_category', categoryId);
}

/**
 * Save a wiki group (group/wiki). Admin-only for create/update.
 */
export async function saveWikiGroup(groupData) {
  const data = {};
  if (groupData.name) data.name = groupData.name;
  if (groupData.description !== undefined) data.description = groupData.description;
  if (groupData.memberIds !== undefined) data.memberIds = groupData.memberIds;
  return decoupledSave('group', 'wiki', data, groupData.id || null);
}

/**
 * Delete a wiki group. Admin-only.
 */
export async function deleteWikiGroup(groupId) {
  return decoupledDelete('group', 'wiki', groupId);
}

/**
 * Save a doc page (node/builder_page rangée dans un répertoire).
 *
 * Le wiki et le Page Builder partagent le bundle : une page documentaire est
 * une `builder_page` qui porte un répertoire. Son CORPS n'est plus un champ
 * texte mais une liste de blocs, écrite par le builder (`/api/builder/save`) —
 * d'où l'absence de `body` dans la charge utile.
 */
export async function saveDocPage(pageData) {
  const data = {};
  if (pageData.title) data.title = pageData.title;
  if (pageData.summary !== undefined) data.summary = pageData.summary;
  if (pageData.directoryId) data.directoryId = pageData.directoryId;
  if (pageData.weight !== undefined) data.weight = pageData.weight;
  if (pageData.category !== undefined) data.category = pageData.category;
  if (pageData.docTags !== undefined) data.docTags = pageData.docTags;
  if (pageData.isTemplate !== undefined) data.isTemplate = pageData.isTemplate;
  if (pageData.icon !== undefined) data.icon = pageData.icon;
  if (pageData.moderationState != null) data.moderationState = pageData.moderationState;
  if (pageData.relatedDocs !== undefined) data.relatedDocs = pageData.relatedDocs;
  if (pageData.relatedTasks !== undefined) data.relatedTasks = pageData.relatedTasks;
  if (pageData.owner !== undefined) data.owner = pageData.owner;
  if (pageData.links !== undefined) data.links = pageData.links;

  const entityId = pageData.id || null;
  // À la création seulement : `directory` dit à Drupal DANS QUEL répertoire on
  // crée, c'est lui qui porte le droit de créer ; `template_source` déclenche
  // le clonage du modèle au presave (résumé, catégorie, étiquettes, icône et
  // surtout les blocs du corps), que le front ne sait pas — et n'a pas — à
  // refaire lui-même.
  const extra = {};
  if (!entityId && data.directoryId) extra.directory = data.directoryId;
  if (!entityId && pageData.templateSource) extra.template_source = pageData.templateSource;
  return decoupledSave('node', 'builder_page', data, entityId, extra);
}

/**
 * Soft-delete a doc page (moves to trash).
 */
export async function deleteDocPage(pageId) {
  return trashEntity('node', 'builder_page', pageId);
}

/**
 * Save a doc comment (comment/doc_comment).
 */
export async function saveDocComment(commentData) {
  const entityId = commentData.id || null;
  if (!entityId && !commentData.pageId) {
    throw new Error('pageId is required when creating a new doc comment');
  }

  const data = {};
  if (commentData.body) data.body = commentData.body;
  if (commentData.pageId) data.pageId = commentData.pageId;
  data.commentedEntityType = 'node';
  data.fieldName = 'field_doc_comments';

  return decoupledSave('comment', 'doc_comment', data, entityId);
}

/**
 * Delete a doc comment.
 */
export async function deleteDocComment(commentId) {
  return decoupledDelete('comment', 'doc_comment', commentId);
}

/**
 * Get resolved access info for a doc directory (grants, roles, children).
 * Throws an ApiError with a `.status` property for HTTP-level failures.
 */
export async function getDirectoryAccess(directoryId) {
  const safeId = encodeURIComponent(String(directoryId));
  const res = await fetch(`/api/doc/directory/${safeId}/access`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const err = new Error(`Failed to load access info (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Save access grants and overrideAccess on a doc directory.
 */
export async function saveDirectoryAccess(directoryId, { accessGrants, overrideAccess }) {
  const safeId = encodeURIComponent(String(directoryId));
  let headers = await getHeaders();
  let res = await fetch(`/api/doc/directory/${safeId}/access`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ accessGrants, overrideAccess }),
  });
  if (res.status === 403) {
    headers = await getHeaders(true);
    res = await fetch(`/api/doc/directory/${safeId}/access`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ accessGrants, overrideAccess }),
    });
  }
  if (!res.ok) throw new Error(`Failed to save access (${res.status})`);
  return res.json();
}

/**
 * Get available subjects (users, project members, roles) for grant assignment.
 */
export async function getDirectoryAccessSubjects(directoryId) {
  const safeId = encodeURIComponent(String(directoryId));
  const res = await fetch(`/api/doc/directory/${safeId}/access/subjects`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Failed to load subjects (${res.status})`);
  return res.json();
}

/**
 * Re-fetch a decoupled API collection and return the raw JSON.
 * Useful for refreshing data after a create/update so that
 * dataProcessors can transform it into the expected front-end format.
 */
export async function fetchCollection(url, bustCache = false) {
  const headers = await getHeaders();
  let finalUrl = url;
  if (bustCache) {
    const sep = url.includes('?') ? '&' : '?';
    finalUrl = `${url}${sep}_=${Date.now()}`;
  }
  const response = await fetch(finalUrl, { headers, credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Fetch failed (${response.status}): ${response.statusText}`);
  }
  return response.json();
}

/**
 * GET an Elasticsearch endpoint and return the raw JSON.
 * @param {string} url - Elasticsearch endpoint URL
 * @param {Object} params - query parameters to append to the URL
 */
export async function elasticsearchGet(url, params = {}) {
  const headers = await getHeaders();
  const query = new URLSearchParams(params).toString();
  const finalUrl = query ? `${url}?${query}` : url;
  const response = await fetch(finalUrl, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
  if (!response.ok) {
    throw new Error(`Elasticsearch request failed (${response.status}): ${response.statusText}`);
  }
  return response.json();
}

// ── Doc tree operations (reorder, move) ─────────────────────────────

/**
 * POST helper with CSRF retry: uses explicit CSRF headers (via getHeaders)
 * so the token is controlled here, not by the global fetch patch.
 * On 403, force-refresh the token and retry once before giving up.
 */
async function postWithCsrfRetry(url, body) {
  let headers = await getHeaders();
  let res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
  });

  if (res.status === 403) {
    headers = await getHeaders(true);
    res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    });
  }

  return res;
}

/**
 * Parse an error body from custom Orbit API endpoints.
 * Supports both { error: "msg" } and { status: { message: "msg" } }.
 */
async function parseApiError(res, fallbackLabel) {
  const body = await res.json().catch(() => ({}));
  const msg = body?.error || body?.status?.message || fallbackLabel;
  const err = new Error(msg);
  err.httpStatus = res.status;
  return err;
}

/**
 * Batch reorder children of a directory.
 * @param {number} parentId - The parent directory TID.
 * @param {{ id: number, type: 'directory'|'page', weight: number }[]} items
 */
export async function reorderDocChildren(parentId, items) {
  const safeId = encodeURIComponent(String(parentId));
  const res = await postWithCsrfRetry(
    `/api/doc/directory/${safeId}/reorder`,
    { items },
  );
  if (!res.ok) throw await parseApiError(res, 'Reorder failed');
  return res.json();
}

/**
 * Fetch the current user's effective permissions on a directory.
 * @param {number} directoryId
 * @returns {Promise<{canView:boolean, canUpdate:boolean, canDelete:boolean, canCreateChildren:boolean, isAdmin:boolean, isOwner:boolean}>}
 */
export async function getDirectoryUserPermissions(directoryId) {
  const res = await fetch(`/api/doc/directory/${encodeURIComponent(directoryId)}/user-permissions`, {
    credentials: 'include',
  });
  if (!res.ok) throw await parseApiError(res, 'Permissions check failed');
  return res.json();
}

/**
 * Move a directory or page to a new parent.
 * @param {{ itemId: number, itemType: 'directory'|'page', targetParentId: number, weight?: number, preserveAccess?: boolean, spaceConfig?: object }} params
 */
export async function moveDocItem(params) {
  const res = await postWithCsrfRetry('/api/doc/tree/move', params);
  if (!res.ok) throw await parseApiError(res, 'Move failed');
  return res.json();
}

/**
 * Preview access diff for a potential move (dry-run).
 * @param {{ itemId: number, itemType: 'directory'|'page', targetParentId: number }} params
 */
export async function moveDocItemPreview(params) {
  const query = new URLSearchParams({
    itemId: String(params.itemId),
    itemType: params.itemType,
    targetParentId: String(params.targetParentId),
  }).toString();
  const res = await fetch(`/api/doc/tree/move-preview?${query}`, {
    credentials: 'include',
  });
  if (!res.ok) throw await parseApiError(res, 'Move preview failed');
  return res.json();
}
